import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'store';
import { ReleaseComponent, ReleaseComponentApi } from 'types/releaseComponent';
import { ReleaseEnvironment } from 'types/releaseEnvironment';

/**
 * 環境一覧画面の状態
 */
interface ListState {
  /**
   * ロード中フラグ
   */
  loading: boolean;
  /**
   * コンポーネント一覧
   */
  releaseComponents: ReleaseComponent[];
  /**
   * 環境一覧
   */
  releaseEnvironments: ReleaseEnvironment[];
  /**
   * 選択されたリリース
   */
  selectedReleaseEnvironment: ReleaseEnvironment | undefined;
}

/**
 * 環境一覧画面の初期状態
 */
const initialState: ListState = {
  loading: false,
  releaseEnvironments: [],
  releaseComponents: [],
  selectedReleaseEnvironment: undefined,
};

/**
 * 環境画面の slice(reducer + action)
 */
export const listSlice = createSlice({
  name: 'list',
  initialState,
  reducers: {
    init: () => initialState,
    loadStarted: (state) => {
      state.loading = true;
    },
    loadEnded: (state) => {
      state.loading = false;
    },
    fetchReleaseEnvironmentSuccess: (
      state,
      action: PayloadAction<{
        releaseEnvironments: ReleaseEnvironment[];
      }>,
    ) => {
      const { releaseEnvironments } = action.payload;
      // For component data Null
      const formData: ReleaseEnvironment[] = releaseEnvironments.map(
        (item) => ({
          components: item.components ? item.components : [],
          manifestVersion: item.manifestVersion,
          name: item.name,
          releasedAt: item.releasedAt,
        }),
      );
      state.releaseEnvironments = formData;
    },
    fetchReleaseComponentSuccess: (
      state,
      action: PayloadAction<{
        releaseComponents: ReleaseComponentApi[];
      }>,
    ) => {
      const { releaseComponents } = action.payload;
      // react-hook-formのformData(array)に対応するためにデータを加工
      const ReleaseComponentForm: ReleaseComponent[] = releaseComponents.map(
        (apiData: ReleaseComponentApi) => ({
          id: apiData.id,
          componentName: apiData.componentName,
          repositoryUrl: apiData.repositoryUrl,
          manifestName: apiData.manifestName,
          targetKeys: apiData.targetKeys.map((targetKey: string) => ({
            value: targetKey,
          })),
        }),
      );
      state.releaseComponents = ReleaseComponentForm;
    },
  },
});

/**
 * 環境一覧画面の action
 */
export const listActions = listSlice.actions;

/**
 * 環境一覧画面の selector（内部用）
 */
const getloading = (state: RootState) => state.releaseEnvironment.list.loading;

const getReleaseEnvironments = (state: RootState) =>
  state.releaseEnvironment.list.releaseEnvironments;

const getReleaseComponents = (state: RootState) =>
  state.releaseComponent.list.releaseComponents;

/**
 * 環境一覧画面の selector
 */
export const listSelectors = {
  loading: getloading,
  releaseEnvironments: getReleaseEnvironments,
  releaseComponents: getReleaseComponents,
} as const;
